import React, { useState, useEffect, useRef } from 'react';
import '../Css/AdminProducts.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faHeading,
  faLink,
  faTag,
  faAlignLeft,
  faFileAlt,
  faCode,
  faFolder,
  faListUl,
  faStar,
  faGift,

  faImages,
  faImage,
  faPhotoVideo,
  faEye,
  faFileCode,
  faToggleOn,
  faExclamationTriangle,
  faFolderOpen,
  faImages as faImagesSolid,
  faChevronLeft,
  faChevronRight,
  
  faUsers,
  faShieldAlt,
  faClipboardList,
  faSearch,
  faVideo,

  faGlobe,

  faLayerGroup,
  faCheck,
  faCrown
} from '@fortawesome/free-solid-svg-icons';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [autoSlideEnabled, setAutoSlideEnabled] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const slideIntervals = useRef({});

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    tagline: '',
    description: '',
    features: '',
    technologies: '',
    demo_url: '',
    documentation_url: '',
    github_url: '',
    category: '',
    license_type: '',
    support_included: true,
    screenshots: '',
    featured_image: null,
    gallery_images: [],
    video_url: '',
    use_cases: '',
    target_audience: '',
    is_featured: false,
    is_active: true,
    meta_title: '',
    meta_description: '',
    created_by: '',
    // Plan fields
    plan_basic_desc: '',
    plan_basic_features: '',
    plan_standard_desc: '',
    plan_standard_features: '',
    plan_premium_desc: '',
    plan_premium_features: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      setFormData(prev => ({ ...prev, created_by: user.id }));
    }
    
    fetchProducts();
    return () => {
      Object.values(slideIntervals.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  useEffect(() => {
    products.forEach(product => {
      const images = getProductImages(product);
      if (images.length > 1 && autoSlideEnabled[product.id]) {
        startAutoSlide(product.id, images.length);
      } else {
        stopAutoSlide(product.id);
      }
    });

    return () => {
      products.forEach(product => {
        stopAutoSlide(product.id);
      });
    };
  }, [products, autoSlideEnabled]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/product.php/get`);
      const data = await response.json();
      if (data.success) {
        setProducts(data.products);
        const initialIndexes = {};
        const initialAutoSlide = {};
        data.products.forEach(product => {
          initialIndexes[product.id] = 0;
          initialAutoSlide[product.id] = true;
        });
        setCurrentImageIndex(initialIndexes);
        setAutoSlideEnabled(initialAutoSlide);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const jsonArrayToString = (jsonArray) => {
    if (!jsonArray) return '';
    try {
      const array = JSON.parse(jsonArray);
      return Array.isArray(array) ? array.join(', ') : '';
    } catch (e) {
      return jsonArray || '';
    }
  };

  const stringToJsonArray = (string) => {
    if (!string) return '[]';
    const items = string.split(',').map(item => item.trim()).filter(item => item !== '');
    return JSON.stringify(items);
  };

  const startAutoSlide = (productId, totalImages) => {
    if (slideIntervals.current[productId]) {
      clearInterval(slideIntervals.current[productId]);
    }

    slideIntervals.current[productId] = setInterval(() => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [productId]: (prev[productId] + 1) % totalImages
      }));
    }, 4000);
  };

  const stopAutoSlide = (productId) => {
    if (slideIntervals.current[productId]) {
      clearInterval(slideIntervals.current[productId]);
      slideIntervals.current[productId] = null;
    }
  };

  const toggleAutoSlide = (productId) => {
    setAutoSlideEnabled(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const nextImage = (productId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[productId];
    
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [productId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: (prev[productId] + 1) % totalImages
    }));

    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [productId]: true
        }));
      }, 10000);
    }
  };

  const prevImage = (productId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[productId];
    
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [productId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: (prev[productId] - 1 + totalImages) % totalImages
    }));

    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [productId]: true
        }));
      }, 10000);
    }
  };

  const goToImage = (productId, index) => {
    const wasAutoSlideEnabled = autoSlideEnabled[productId];
    
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [productId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: index
    }));

    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [productId]: true
        }));
      }, 10000);
    }
  };

  const getProductImages = (product) => {
    const images = [];
    
    if (product.gallery_images_urls) {
      let galleryUrls = product.gallery_images_urls;
      
      if (typeof galleryUrls === 'string') {
        try {
          galleryUrls = JSON.parse(galleryUrls);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
          galleryUrls = [galleryUrls];
        }
      }
      
      if (Array.isArray(galleryUrls)) {
        galleryUrls.forEach(url => {
          if (url && url.trim() !== '') {
            images.push({
              url: url,
              type: 'gallery'
            });
          }
        });
      }
    }
    
    if (product.featured_image_url && images.length === 0) {
      images.push({
        url: product.featured_image_url,
        type: 'featured'
      });
    }
    
    return images;
  };

  // Function to get plan features as array
  const getPlanFeaturesArray = (planFeatures) => {
    if (!planFeatures) return [];
    try {
      return JSON.parse(planFeatures);
    } catch (e) {
      return planFeatures.split(',').map(f => f.trim()).filter(f => f);
    }
  };

  // Function to display plans in product card
  const renderPlanBadges = (product) => {
    const plans = [];
    if (product.plan_basic_desc) plans.push('Basic');
    if (product.plan_standard_desc) plans.push('Standard');
    if (product.plan_premium_desc) plans.push('Premium');
    
    if (plans.length === 0) return null;
    
    return (
      <div className="product-plans-section mb-2">
        <div className="product-plan-label">
          <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
          Available Plans:
        </div>
        <div className="product-plan-badges">
          {plans.map((plan, idx) => (
            <span key={idx} className={`product-plan-badge ${plan.toLowerCase()}`}>
              {plan === 'Premium' && <FontAwesomeIcon icon={faCrown} className="me-1" />}
              {plan}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditMode(true);
      setSelectedProduct(product);
      
      let existingGallery = [];
      let existingFeaturedImage = null;

      if (product.gallery_images) {
        try {
          existingGallery = JSON.parse(product.gallery_images);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
        }
      }

      if (product.featured_image_url) {
        existingFeaturedImage = product.featured_image_url;
      }

      // Get plan features as strings
      const basicFeaturesArray = product.plan_basic_features_array || getPlanFeaturesArray(product.plan_basic_features);
      const standardFeaturesArray = product.plan_standard_features_array || getPlanFeaturesArray(product.plan_standard_features);
      const premiumFeaturesArray = product.plan_premium_features_array || getPlanFeaturesArray(product.plan_premium_features);

      setFormData({
        name: product.name || '',
        slug: product.slug || '',
        tagline: product.tagline || '',
        description: product.description || '',
        features: jsonArrayToString(product.features),
        technologies: jsonArrayToString(product.technologies),
        demo_url: product.demo_url || '',
        documentation_url: product.documentation_url || '',
        github_url: product.github_url || '',
        category: product.category || '',
        license_type: product.license_type || '',
        support_included: product.support_included !== undefined ? product.support_included : true,
        screenshots: jsonArrayToString(product.screenshots),
        featured_image: null,
        gallery_images: [],
        video_url: product.video_url || '',
        use_cases: jsonArrayToString(product.use_cases),
        target_audience: product.target_audience || '',
        is_featured: product.is_featured || false,
        is_active: product.is_active !== undefined ? product.is_active : true,
        meta_title: product.meta_title || '',
        meta_description: product.meta_description || '',
        created_by: product.created_by || currentUser?.id || '',
        // Plan fields
        plan_basic_desc: product.plan_basic_desc || '',
        plan_basic_features: Array.isArray(basicFeaturesArray) ? basicFeaturesArray.join(', ') : basicFeaturesArray || '',
        plan_standard_desc: product.plan_standard_desc || '',
        plan_standard_features: Array.isArray(standardFeaturesArray) ? standardFeaturesArray.join(', ') : standardFeaturesArray || '',
        plan_premium_desc: product.plan_premium_desc || '',
        plan_premium_features: Array.isArray(premiumFeaturesArray) ? premiumFeaturesArray.join(', ') : premiumFeaturesArray || ''
      });

      const previews = existingGallery.map(filename => ({
        name: filename,
        url: `${API_URL}/uploads/products/${filename}`,
        isExisting: true
      }));
      setGalleryPreviews(previews);
      setFeaturedImagePreview(existingFeaturedImage);

    } else {
      setEditMode(false);
      setSelectedProduct(null);
      setFormData({
        name: '',
        slug: '',
        tagline: '',
        description: '',
        features: '',
        technologies: '',
        demo_url: '',
        documentation_url: '',
        github_url: '',
        category: '',
        license_type: '',
        support_included: true,
        screenshots: '',
        featured_image: null,
        gallery_images: [],
        video_url: '',
        use_cases: '',
        target_audience: '',
        is_featured: false,
        is_active: true,
        meta_title: '',
        meta_description: '',
        created_by: currentUser?.id || '',
        // Plan fields
        plan_basic_desc: '',
        plan_basic_features: '',
        plan_standard_desc: '',
        plan_standard_features: '',
        plan_premium_desc: '',
        plan_premium_features: ''
      });
      setGalleryPreviews([]);
      setFeaturedImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedProduct(null);
    setGalleryPreviews([]);
    setFeaturedImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'file') {
      if (name === 'gallery_images') {
        const newFiles = Array.from(files);
        setFormData({ 
          ...formData, 
          gallery_images: [...formData.gallery_images, ...newFiles] 
        });
        
        newFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = (e) => {
            setGalleryPreviews(prev => [...prev, {
              name: file.name,
              url: e.target.result,
              isExisting: false,
              file: file
            }]);
          };
          reader.readAsDataURL(file);
        });
      } else if (name === 'featured_image') {
        setFormData({ ...formData, [name]: files[0] });
        
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFeaturedImagePreview(e.target.result);
          };
          reader.readAsDataURL(files[0]);
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const removeGalleryImage = (index) => {
    const imageToRemove = galleryPreviews[index];
    const updatedPreviews = galleryPreviews.filter((_, i) => i !== index);
    setGalleryPreviews(updatedPreviews);
    
    if (!imageToRemove.isExisting) {
      let newFileIndex = 0;
      for (let i = 0; i < index; i++) {
        if (!galleryPreviews[i].isExisting) {
          newFileIndex++;
        }
      }
      
      const updatedFiles = [...formData.gallery_images];
      updatedFiles.splice(newFileIndex, 1);
      setFormData({ ...formData, gallery_images: updatedFiles });
    } 
  };

  const removeFeaturedImage = () => {
    setFormData({ ...formData, featured_image: null });
    setFeaturedImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser?.id) {
      toast.error('Please login to create/edit products');
      return;
    }

    const formDataToSend = new FormData();
    
    const processedFormData = {
      ...formData,
      features: stringToJsonArray(formData.features),
      technologies: stringToJsonArray(formData.technologies),
      screenshots: stringToJsonArray(formData.screenshots),
      use_cases: stringToJsonArray(formData.use_cases),
      plan_basic_features: stringToJsonArray(formData.plan_basic_features),
      plan_standard_features: stringToJsonArray(formData.plan_standard_features),
      plan_premium_features: stringToJsonArray(formData.plan_premium_features),
      created_by: currentUser.id
    };
    
    Object.keys(processedFormData).forEach(key => {
      if (key !== 'featured_image' && key !== 'gallery_images') {
        if (processedFormData[key] !== null && processedFormData[key] !== '') {
          if (typeof processedFormData[key] === 'boolean') {
            formDataToSend.append(key, processedFormData[key] ? '1' : '0');
          } else {
            formDataToSend.append(key, processedFormData[key]);
          }
        }
      }
    });

    if (processedFormData.featured_image) {
      formDataToSend.append('featured_image', processedFormData.featured_image);
    }

    if (editMode) {
      const existingImagesToKeep = galleryPreviews
        .filter(preview => preview.isExisting)
        .map(preview => preview.name);
      if (existingImagesToKeep.length > 0) {
        formDataToSend.append('existing_gallery_images', JSON.stringify(existingImagesToKeep));
      }
      
      if (processedFormData.gallery_images.length > 0) {
        processedFormData.gallery_images.forEach((file, index) => {
          formDataToSend.append('gallery_images[]', file);
        });
      }
    } else { 
      if (processedFormData.gallery_images.length > 0) {
        processedFormData.gallery_images.forEach((file, index) => {
          formDataToSend.append('gallery_images[]', file);
        });
      }
    }

    try {
      let url, method;
      if (editMode) {
        url = `${API_URL}/product.php/put/${selectedProduct.id}`;
        method = 'POST';
      } else {
        url = `${API_URL}/product.php/post`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        body: formDataToSend
      });

      const data = await response.json();
    
      if (data.success) {
        fetchProducts();
        handleCloseModal();
        if (editMode) {
          toast.success('Product updated successfully!', { className: 'product-toast-edit' });
        } else {
          toast.success('Product added successfully!', { className: 'product-toast-add' });
        }
      } else {
        toast.error(data.error || 'An error occurred', { className: 'product-toast-error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form', { className: 'product-toast-error' });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/product.php/delete/${deleteId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchProducts();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('Product deleted successfully!', { className: 'product-toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'product-toast-error' });
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('An error occurred while deleting the product', { className: 'product-toast-error' });
    }
  };

  return (
    <div className="products-admin-wrapper">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="products-admin-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="products-admin-title">
                <FontAwesomeIcon icon={faBox} className="me-3" />
                Products Management
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="products-add-button" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Product
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-5">
        {loading ? (
          <div className="products-loading-state">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="products-empty-state">
            <div className="products-empty-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>
            <p className="products-empty-text">No products found. Create your first product!</p>
          </div>
        ) : (
          <div className="row">
            {products.map((product) => {
              const images = getProductImages(product);
              const currentIndex = currentImageIndex[product.id] || 0;
              const hasMultipleImages = images.length > 1;
              
              return (
                <div key={product.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="product-display-card">
                    {images.length > 0 ? (
                      <div className="product-display-image">
                        <img 
                          src={`${API_URL}/${images[currentIndex].url}`} 
                          alt={product.name} 
                        />
                        
                        {hasMultipleImages && (
                          <>
                            <button 
                              className="product-slider-prev"
                              onClick={(e) => {
                                e.stopPropagation();
                                prevImage(product.id, images.length);
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button 
                              className="product-slider-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                nextImage(product.id, images.length);
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                            
                            <div className="product-image-count">
                              <FontAwesomeIcon icon={faImagesSolid} className="me-1" />
                              {currentIndex + 1} / {images.length}
                            </div>
                            
                            <div className="product-slider-indicators">
                              {images.map((_, index) => (
                                <button
                                  key={index}
                                  className={`product-indicator ${index === currentIndex ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    goToImage(product.id, index);
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="product-display-image">
                        <div className="product-no-image">
                          <FontAwesomeIcon icon={faImage} />
                          <span>No Image</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="product-display-content">
                      <div className="product-header-section">
                        <h5 className="product-display-name">{product.name}</h5>
                        {product.is_featured && (
                          <span className="product-featured-badge">
                            <FontAwesomeIcon icon={faStar} />
                          </span>
                        )}
                      </div>
                      
                      {product.tagline && <p className="product-display-tagline">{product.tagline}</p>}
                      
                      <p className="product-display-desc">
                        {product.description?.substring(0, 120)}...
                      </p>

                      {/* Plans Section
                      {renderPlanBadges(product)} */}

                      {/* Category & Status */}
                      <div className="product-meta-row mb-3">
                        {product.category && (
                          <span className="product-display-category">
                            <FontAwesomeIcon icon={faTag} className="me-1" />
                            {product.category}
                          </span>
                        )}
                        <span className={`product-status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                          <FontAwesomeIcon icon={product.is_active ? faToggleOn : faTimes} className="me-1" />
                          {product.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {/* Technologies */}
                      {product.technologies && jsonArrayToString(product.technologies) && (
                        <div className="product-tech-section mb-3">
                          <div className="product-tech-label">
                            <FontAwesomeIcon icon={faCode} className="me-1" />
                            Tech Stack
                          </div>
                          <div className="product-tech-tags">
                            {jsonArrayToString(product.technologies).split(',').slice(0, 3).map((tech, idx) => (
                              <span key={idx} className="product-tech-tag">
                                {tech.trim()}
                              </span>
                            ))}
                            {jsonArrayToString(product.technologies).split(',').length > 3 && (
                              <span className="product-tech-tag more">
                                +{jsonArrayToString(product.technologies).split(',').length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* License & Support */}
                      <div className="product-license-section mb-3">
                        {product.license_type && (
                          <span className="license-info">
                            <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                            {product.license_type}
                          </span>
                        )}
                        {product.support_included && (
                          <span className="support-info">
                            <FontAwesomeIcon icon={faGift} className="me-1" />
                            Support Included
                          </span>
                        )}
                      </div>

                      {/* Quick Links */}
                      <div className="product-footer-info">
                        <div className="product-quick-links">
                          {product.demo_url && (
                            <a href={product.demo_url} target="_blank" rel="noopener noreferrer" 
                               className="product-quick-link" title="View Demo"
                               onClick={(e) => e.stopPropagation()}>
                              <FontAwesomeIcon icon={faEye} />
                            </a>
                          )}
                          {product.github_url && (
                            <a href={product.github_url} target="_blank" rel="noopener noreferrer" 
                               className="product-quick-link" title="GitHub"
                               onClick={(e) => e.stopPropagation()}>
                              <FontAwesomeIcon icon={faFileCode} />
                            </a>
                          )}
                          {product.documentation_url && (
                            <a href={product.documentation_url} target="_blank" rel="noopener noreferrer" 
                               className="product-quick-link" title="Documentation"
                               onClick={(e) => e.stopPropagation()}>
                              <FontAwesomeIcon icon={faFileAlt} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="product-display-actions">
                      <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={() => handleOpenModal(product)}
                        title="Edit Product"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDeleteClick(product.id)}
                        title="Delete Product"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content product-modal-content">
              <div className="modal-header product-modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Product' : 'Add New Product'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body product-modal-body">
                <form onSubmit={handleSubmit} id="productForm">
                  <div className="row">
                    {/* Basic Information */}
                    <div className="col-12 mb-4">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Basic Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Product Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        placeholder="Enter product name"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="slug" 
                        value={formData.slug} 
                        onChange={handleInputChange} 
                        placeholder="product-url-slug"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faTag} className="me-1" />
                        Tagline
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="tagline" 
                        value={formData.tagline} 
                        onChange={handleInputChange} 
                        placeholder="A catchy tagline for your product"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea 
                        className="form-control product-textarea" 
                        name="description" 
                        value={formData.description} 
                        onChange={handleInputChange} 
                        rows="4" 
                        placeholder="Detailed description of your product"
                        required
                      ></textarea>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faFolder} className="me-1" />
                        Category
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="category" 
                        value={formData.category} 
                        onChange={handleInputChange}
                        placeholder="Software, Template, Plugin, etc."
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        Target Audience
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="target_audience" 
                        value={formData.target_audience} 
                        onChange={handleInputChange}
                        placeholder="Developers, Businesses, Startups"
                      />
                    </div>

                    {/* Media Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faImages} className="me-2" />
                        Media & Images
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Featured Image
                      </label>
                      <input 
                        type="file" 
                        className="form-control product-input" 
                        name="featured_image" 
                        onChange={handleInputChange} 
                        accept="image/*"
                      />
                      
                      {featuredImagePreview && (
                        <div className="mt-3 position-relative">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Featured Image Preview:
                          </h6>
                          <div className="position-relative d-inline-block">
                            <img 
                              src={`${API_URL}/${featuredImagePreview}`} 
                              alt="Featured preview"
                              className="product-featured-preview"
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={removeFeaturedImage}
                              title="Remove image"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faPhotoVideo} className="me-1" />
                        Gallery Images
                      </label>
                      <input 
                        type="file" 
                        className="form-control product-input" 
                        name="gallery_images" 
                        onChange={handleInputChange} 
                        accept="image/*" 
                        multiple
                      />
                      
                      {galleryPreviews.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Gallery Previews ({galleryPreviews.length}):
                          </h6>
                          <div className="row">
                            {galleryPreviews.map((preview, index) => (
                              <div key={index} className="col-md-4 col-6 mb-2 position-relative">
                                <img 
                                  src={preview.url} 
                                  alt={`Gallery ${index + 1}`}
                                  className="product-gallery-preview"
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  onClick={() => removeGalleryImage(index)}
                                  title="Remove image"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faVideo} className="me-1" />
                        Video URL
                      </label>
                      <input 
                        type="url" 
                        className="form-control product-input" 
                        name="video_url" 
                        value={formData.video_url} 
                        onChange={handleInputChange}
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faImages} className="me-1" />
                        Screenshots (comma-separated URLs)
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="screenshots" 
                        value={formData.screenshots} 
                        onChange={handleInputChange}
                        placeholder="screenshot1.jpg, screenshot2.jpg"
                      />
                    </div>

                    {/* Features & Details */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faListUl} className="me-2" />
                        Features & Details
                      </h6>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faStar} className="me-1" />
                        Features 
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="features" 
                        value={formData.features} 
                        onChange={handleInputChange} 
                        placeholder="Feature 1, Feature 2, Feature 3"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faCode} className="me-1" />
                        Technologies 
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="technologies" 
                        value={formData.technologies} 
                        onChange={handleInputChange} 
                        placeholder="React, Node.js, MongoDB"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                        Use Cases 
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="use_cases" 
                        value={formData.use_cases} 
                        onChange={handleInputChange} 
                        placeholder="E-commerce, Blogging, Portfolio"
                      />
                    </div>

                    {/* Plans Section - NEW */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                        Pricing Plans
                      </h6>
                      
                    </div>

                    {/* Basic Plan */}
                    <div className="col-12 mb-4">
                      <div className="plan-section">
                        <h6 className="plan-title">
                          <span className="plan-icon basic">
                            <FontAwesomeIcon icon={faCheck} />
                          </span>
                          Basic Plan
                        </h6>
                        <div className="row">
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Description
                            </label>
                            <input 
                              type="text" 
                              className="form-control product-input" 
                              name="plan_basic_desc" 
                              value={formData.plan_basic_desc} 
                              onChange={handleInputChange}
                              placeholder="Description of basic plan features"
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Features
                            </label>
                            <textarea 
                              className="form-control product-textarea" 
                              name="plan_basic_features" 
                              value={formData.plan_basic_features} 
                              onChange={handleInputChange}
                              rows="2"
                              placeholder="Feature 1, Feature 2, Feature 3"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Standard Plan */}
                    <div className="col-12 mb-4">
                      <div className="plan-section">
                        <h6 className="plan-title">
                          <span className="plan-icon standard">
                            <FontAwesomeIcon icon={faStar} />
                          </span>
                          Standard Plan
                        </h6>
                        <div className="row">
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Description
                            </label>
                            <input 
                              type="text" 
                              className="form-control product-input" 
                              name="plan_standard_desc" 
                              value={formData.plan_standard_desc} 
                              onChange={handleInputChange}
                              placeholder="Description of standard plan features"
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Features
                            </label>
                            <textarea 
                              className="form-control product-textarea" 
                              name="plan_standard_features" 
                              value={formData.plan_standard_features} 
                              onChange={handleInputChange}
                              rows="2"
                              placeholder="Feature 1, Feature 2, Feature 3"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Premium Plan */}
                    <div className="col-12 mb-4">
                      <div className="plan-section">
                        <h6 className="plan-title">
                          <span className="plan-icon premium">
                            <FontAwesomeIcon icon={faCrown} />
                          </span>
                          Premium Plan
                        </h6>
                        <div className="row">
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Description
                            </label>
                            <input 
                              type="text" 
                              className="form-control product-input" 
                              name="plan_premium_desc" 
                              value={formData.plan_premium_desc} 
                              onChange={handleInputChange}
                              placeholder="Description of premium plan features"
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="product-form-label">
                              Plan Features (comma-separated)
                            </label>
                            <textarea 
                              className="form-control product-textarea" 
                              name="plan_premium_features" 
                              value={formData.plan_premium_features} 
                              onChange={handleInputChange}
                              rows="2"
                              placeholder="Feature 1, Feature 2, Feature 3"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* URLs Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faGlobe} className="me-2" />
                        Links & URLs
                      </h6>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        Demo URL
                      </label>
                      <input 
                        type="url" 
                        className="form-control product-input" 
                        name="demo_url" 
                        value={formData.demo_url} 
                        onChange={handleInputChange}
                        placeholder="https://demo.example.com"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Documentation URL
                      </label>
                      <input 
                        type="url" 
                        className="form-control product-input" 
                        name="documentation_url" 
                        value={formData.documentation_url} 
                        onChange={handleInputChange}
                        placeholder="https://docs.example.com"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faFileCode} className="me-1" />
                        GitHub URL
                      </label>
                      <input 
                        type="url" 
                        className="form-control product-input" 
                        name="github_url" 
                        value={formData.github_url} 
                        onChange={handleInputChange}
                        placeholder="https://github.com/username/repo"
                      />
                    </div>

                    {/* License & Support Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                        License & Support
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                        License Type
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="license_type" 
                        value={formData.license_type} 
                        onChange={handleInputChange}
                        placeholder="MIT, GPL, Commercial, etc."
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input 
                          className="form-check-input product-switch" 
                          type="checkbox" 
                          name="support_included" 
                          checked={formData.support_included} 
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label product-form-label">
                          <FontAwesomeIcon icon={faGift} className="me-1" />
                          Support Included
                        </label>
                      </div>
                    </div>

                    {/* Settings Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faToggleOn} className="me-2" />
                        Settings & Display
                      </h6>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input 
                          className="form-check-input product-switch" 
                          type="checkbox" 
                          name="is_featured" 
                          checked={formData.is_featured} 
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label product-form-label">
                          <FontAwesomeIcon icon={faStar} className="me-1" />
                          Featured Product
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input 
                          className="form-check-input product-switch" 
                          type="checkbox" 
                          name="is_active" 
                          checked={formData.is_active} 
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label product-form-label">
                          <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                          Active Product
                        </label>
                      </div>
                    </div>

                    {/* SEO Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="product-section-title">
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        SEO Information
                      </h6>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Meta Title
                      </label>
                      <input 
                        type="text" 
                        className="form-control product-input" 
                        name="meta_title" 
                        value={formData.meta_title} 
                        onChange={handleInputChange}
                        placeholder="SEO title for search engines"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="product-form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Meta Description
                      </label>
                      <textarea 
                        className="form-control product-textarea" 
                        name="meta_description" 
                        value={formData.meta_description} 
                        onChange={handleInputChange}
                        rows="2"
                        placeholder="SEO description for search engines"
                      ></textarea>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer product-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="productForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content product-modal-content">
              <div className="modal-header product-delete-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this product? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}